// Mod/プラグインタブの「手動で入れたもの」。Modrinth などに無い自作の jar を一覧し、入れ替え・取り外しをする。
import { useRef, useState } from 'react';
import type { ManualAddonKind } from '@mcpanel/core';
import { api3 } from '../api/client-p3';
import { apiM } from '../api/client-maint';
import { errorMessage, useApp } from '../app-context';
import { formatBytes, formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';
import { Spinner } from './feedback';
import { Empty, ErrorAlert } from './ui';

export function ManualAddons({ serverId, kind }: { serverId: string; kind: ManualAddonKind }) {
  const { canOperate, notify } = useApp();
  const list = usePoll(() => api3.manualAddons(serverId), 60000, [serverId]);
  const [busy, setBusy] = useState(false);
  const [restart, setRestart] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const word = kind === 'plugin' ? 'プラグイン' : 'Mod';
  const addons = (list.data?.addons ?? []).filter((addon) => addon.kind === kind);
  const mods = kind === 'mod';
  // サーバーから外したあとも配り続けている写し（VM に問い合わせられないときは出さない）
  const orphans =
    mods && !list.data?.unavailable
      ? (list.data?.distributed ?? []).filter((jar) => !addons.some((addon) => addon.identity?.id === jar.modId))
      : [];
  const [distributing, setDistributing] = useState<string>();

  const distribute = async (fileName: string, side: 'required' | 'optional' | null) => {
    if (
      side &&
      !window.confirm(
        `${fileName} をランチャーでプレイヤーに配ります。配ってよい（再配布してよい）jar か確かめてください。サーバーは再起動しません。起動テストに受かったあと、ランチャーに届きます。`,
      )
    )
      return;
    setDistributing(fileName);
    try {
      await api3.setManualAddonDistribution(serverId, fileName, side);
      notify(
        side
          ? `${fileName} を配布物に入れました。起動テストに受かったら、ランチャーに届きます。`
          : `${fileName} を配るのをやめました（サーバーの jar はそのままです）。`,
      );
      await list.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setDistributing(undefined);
    }
  };

  const replace = async (files: FileList | null) => {
    const file = files?.[0];
    if (input.current) input.current.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const result = await api3.replaceManualAddon(serverId, { kind, file, restart });
      const name = `${result.identity.name ?? result.identity.id} ${result.identity.version ?? ''}`.trim();
      const moved = result.trashed.map((t) => t.fileName).join('、');
      notify(
        `${name} を入れました。${moved ? `古い jar（${moved}）は .trash へ移しました。` : ''}${result.note}${result.distributed ? '。ランチャーで配る jar も新しくしました' : ''}`,
      );
      await list.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (path: string, fileName: string) => {
    if (!window.confirm(`${fileName} を外しますか？（/data/.trash へ移します。次の再起動で反映されます）`)) return;
    try {
      await apiM.trashFile(serverId, path);
      await list.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  return (
    <section className="card" data-search-id={kind === 'plugin' ? 'manual-plugins' : 'manual-mods'}>
      <div className="card-header">
        <h2>
          手動で入れた{word}（{addons.length}）
        </h2>
        <span className="small faint">
          Modrinth などに無い自作・非公式の jar
          {mods && '。「配る」にした jar は、ランチャーでプレイヤーの PC にも入ります'}
        </span>
      </div>
      <ErrorAlert error={list.error} />
      {list.data?.unavailable && <div className="card-body small muted">{list.data.unavailable}</div>}
      {addons.length === 0 ? (
        !list.data?.unavailable && <Empty>手動で入れた{word}はありません。</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{word}</th>
                <th>ファイル</th>
                {mods && <th data-search-id="manual-mod-distribution">プレイヤーの PC に</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {addons.map((addon) => (
                <tr key={addon.path}>
                  <td>
                    <div>{addon.identity?.name ?? addon.identity?.id ?? addon.fileName}</div>
                    <div className="small faint">{addon.identity?.version ?? addon.error ?? ''}</div>
                  </td>
                  <td className="small muted">
                    <div>{addon.fileName}</div>
                    <div className="faint">
                      {formatBytes(addon.sizeBytes)} ・ {formatDateTime(addon.modifiedAt)}
                    </div>
                  </td>
                  {mods && (
                    <td className="small muted">
                      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                        <select
                          className="input"
                          style={{ width: 200 }}
                          aria-label={`${addon.fileName} をプレイヤーに配るか`}
                          title="ランチャーでプレイヤーの PC にも入れるか"
                          disabled={!canOperate || !addon.identity || distributing !== undefined}
                          value={addon.distribution?.side ?? 'none'}
                          onChange={(e) =>
                            void distribute(
                              addon.fileName,
                              e.target.value === 'none' ? null : (e.target.value as 'required' | 'optional'),
                            )
                          }
                        >
                          <option value="none">配らない（サーバーだけ）</option>
                          <option value="required">配る（必須）</option>
                          <option value="optional">配る（任意）</option>
                        </select>
                        {distributing === addon.fileName && <Spinner />}
                      </div>
                      {addon.distribution?.stale && (
                        <div className="small" style={{ color: 'var(--warn)', marginTop: 4 }}>
                          配っている写し（{addon.distribution.version ?? '版不明'}）とサーバーの jar が違うようです。
                          {canOperate && (
                            <button
                              type="button"
                              className="btn ghost small"
                              disabled={distributing !== undefined}
                              onClick={() => void distribute(addon.fileName, addon.distribution!.side)}
                            >
                              写しを新しくする
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                  <td style={{ textAlign: 'right' }}>
                    {canOperate && (
                      <button
                        className="btn ghost small danger"
                        onClick={() => void remove(addon.path, addon.fileName)}
                      >
                        外す
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {orphans.length > 0 && (
        <div className="card-body stack">
          <div className="small muted">サーバーから外したあとも、ランチャーで配り続けている jar があります。</div>
          {orphans.map((jar) => (
            <div key={jar.modId} className="row" style={{ gap: 8 }}>
              <span>
                {jar.name ?? jar.modId} {jar.version ?? ''}（{jar.fileName}）
              </span>
              {canOperate && (
                <button
                  type="button"
                  className="btn ghost small danger"
                  disabled={distributing !== undefined}
                  onClick={() => void distribute(jar.fileName, null)}
                >
                  配るのをやめる
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {canOperate && (
        <div className="card-body stack">
          <div className="row" style={{ gap: 12 }}>
            <button className="btn small" disabled={busy} onClick={() => input.current?.click()}>
              {busy && <Spinner />}
              jar を入れる・更新する
            </button>
            <span className="small faint">
              jar の中の名前が同じ古い jar は、/data/.trash へ移してから入れ替えます。
            </span>
          </div>
          <label className="check">
            <input type="checkbox" checked={restart} onChange={(e) => setRestart(e.target.checked)} />
            <span>入れたあと再起動する（起動中のとき。外すときは次の再起動で反映）</span>
          </label>
          <input
            ref={input}
            type="file"
            accept=".jar"
            hidden
            aria-label={`${word}の jar`}
            onChange={(e) => void replace(e.target.files)}
          />
        </div>
      )}
    </section>
  );
}
